"use client";

import { useEffect, useRef, useState } from "react";
import {
  Image as ImageIcon,
  Upload,
  Trash2,
  Copy,
  X,
  Loader2,
  Sparkles,
  Eye,
  Check,
} from "lucide-react";
import {
  PageHeader,
  Card,
  SearchField,
  Segmented,
  Spinner,
  Empty,
  Button,
  ConfirmDialog,
  useToast,
} from "@/components/ui";
import { adminMediaApi, type StaticDefaultsManifest } from "@/lib/adminApi";
import { resolveAssetUrl } from "@/lib/assetUrl";
import { cn } from "@/lib/utils";

type Kind = "bg" | "fpv_flower" | "fpv_ring" | "fpv_dinner";

interface Section {
  kind: Kind;
  label: string;
  description: string;
  /** Tailwind gradient on the section's tab + the upload button glow. */
  accent: string;
  /** Dot color used on the section badge in the header. */
  dot: string;
}

const SECTIONS: Section[] = [
  { kind: "bg",        label: "Backgrounds",   description: "Full-bleed poster / cover images for proposals.",   accent: "from-pink-500 via-rose-500 to-amber-400",     dot: "bg-pink-500" },
  { kind: "fpv_flower", label: "FPV · Flowers",  description: "Cinematic flower-petal shots used as photo slides.", accent: "from-pink-500 via-fuchsia-500 to-purple-500", dot: "bg-fuchsia-500" },
  { kind: "fpv_ring",   label: "FPV · Ring",     description: "Close-up ring reveal animations.",                     accent: "from-amber-500 via-rose-500 to-pink-500",    dot: "bg-amber-500" },
  { kind: "fpv_dinner", label: "FPV · Dinner",   description: "Date-night / candlelight table shots.",               accent: "from-rose-500 via-pink-500 to-amber-400",    dot: "bg-rose-500" },
];

const sectionIndex = (k: Kind) => SECTIONS.findIndex((s) => s.kind === k);

export default function MediaPage() {
  const [manifest, setManifest] = useState<StaticDefaultsManifest | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeKind, setActiveKind] = useState<Kind>("bg");
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState<{ kind: Kind; name: string } | null>(null);

  const { showToast } = useToast();

  const fetchManifest = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const data = await adminMediaApi.manifest();
      if (!data.success) throw new Error("Manifest returned failure");
      setManifest(data);
    } catch (e: any) {
      showToast({ title: "Failed to load media", description: e?.message, variant: "error" });
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  useEffect(() => {
    void fetchManifest();
  }, []);

  const activeSection = SECTIONS.find((s) => s.kind === activeKind)!;
  const activeFiles = (manifest?.kinds[activeKind] ?? []).filter((f) =>
    !search.trim() || f.toLowerCase().includes(search.trim().toLowerCase()),
  );
  const totalFiles = SECTIONS.reduce(
    (sum, s) => sum + (manifest?.kinds[s.kind]?.length ?? 0),
    0,
  );

  return (
    <div>
      <PageHeader
        eyebrow="Library"
        title="Media Library"
        subtitle={
          manifest
            ? `${totalFiles} files served at /static-defaults/ — pick them from the template editor's "Browse defaults" picker.`
            : "Loading media manifest…"
        }
        actions={
          <div className="w-full sm:w-72">
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Search filenames…"
            />
          </div>
        }
      />

      {/* ─── Tab bar — Segmented with file counts per kind, matches /templates ─ */}
      <div className="mt-4">
        <Segmented<Kind>
          value={activeKind}
          onChange={setActiveKind}
          options={SECTIONS.map((s) => ({
            value: s.kind,
            label: (
              <span className="inline-flex items-center gap-2">
                <span className={cn(
                  "h-2 w-2 shrink-0 rounded-full",
                  s.dot,
                )} />
                {s.label}
                <span className="opacity-60">
                  {(manifest?.kinds[s.kind] ?? []).length}
                </span>
              </span>
            ),
          }))}
        />
      </div>

      {/* ─── Active section — content cross-fades with a small slide ─── */}
      <div className="relative">
        {SECTIONS.map((section) => (
          <SectionPanel
            key={section.kind}
            section={section}
            active={activeKind === section.kind}
            files={(manifest?.kinds[section.kind] ?? []).filter((f) =>
              !search.trim() || f.toLowerCase().includes(search.trim().toLowerCase()),
            )}
            loading={loading && !manifest}
            onChanged={() => fetchManifest(false)}
            onPreview={(name) => setPreview({ kind: section.kind, name })}
          />
        ))}
      </div>

      <PreviewLightbox
        preview={preview}
        onClose={() => setPreview(null)}
        onChanged={() => fetchManifest(false)}
      />
    </div>
  );
}

/* ─── Section panel — fade + slide on tab change ──────────────────── */

function SectionPanel({
  section, active, files, loading, onChanged, onPreview,
}: {
  section: Section;
  active: boolean;
  files: string[];
  loading: boolean;
  onChanged: () => void;
  onPreview: (filename: string) => void;
}) {
  return (
    <div
      className={cn(
        "mt-4 transition-all duration-300 ease-out",
        active
          ? "opacity-100 translate-y-0 pointer-events-auto relative"
          : "opacity-0 translate-y-2 pointer-events-none absolute inset-0",
      )}
      aria-hidden={!active}
    >
      <SectionBody
        section={section}
        files={files}
        loading={loading}
        onChanged={onChanged}
        onPreview={onPreview}
      />
    </div>
  );
}

function SectionBody({
  section, files, loading, onChanged, onPreview,
}: {
  section: Section;
  files: string[];
  loading: boolean;
  onChanged: () => void;
  onPreview: (filename: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { showToast } = useToast();

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      const res = await adminMediaApi.upload(section.kind, file);
      if (!res.success) throw new Error("Upload returned failure");
      showToast({ title: "File uploaded", description: file.name, variant: "success" });
      onChanged();
    } catch (e: any) {
      showToast({ title: "Upload failed", description: e?.message, variant: "error" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const filename = confirmDelete;
    setConfirmDelete(null);
    try {
      await adminMediaApi.delete(section.kind, filename);
      showToast({ title: "File deleted", description: filename, variant: "success" });
      onChanged();
    } catch (e: any) {
      showToast({ title: "Delete failed", description: e?.message, variant: "error" });
    }
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  };

  return (
    <Card>
      <div className="p-5">
        {/* Section header */}
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className={cn("h-2 w-2 rounded-full", section.dot)} />
              <h2 className="text-base font-bold text-zinc-900 dark:text-zinc-50">{section.label}</h2>
              <span className="font-mono text-[10px] text-zinc-400">
                {files.length} {files.length === 1 ? "file" : "files"}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">{section.description}</p>
          </div>

          <label className={cn(
            "inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold text-white shadow-[0_4px_14px_-4px_rgba(255,51,119,0.55)] transition hover:scale-[1.02] active:scale-[0.99]",
            "bg-gradient-to-br", section.accent,
          )}>
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            Upload
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={onPickFile}
              disabled={uploading}
            />
          </label>
        </div>

        {/* ─── 9:16 portrait card grid ───────────────────────────────── */}
        {loading && files.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Spinner className="!h-6 !w-6" />
          </div>
        ) : files.length === 0 ? (
          <Empty
            icon={<ImageIcon className="h-5 w-5" />}
            title="No images yet"
            message="Upload images to populate this section — they'll appear in the template editor's picker."
          />
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {files.map((file) => (
              <PortraitTile
                key={file}
                filename={file}
                kind={section.kind}
                onPreview={() => onPreview(file)}
                onDelete={() => setConfirmDelete(file)}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title={`Delete ${confirmDelete ?? ""}?`}
        description="This file will be removed from the public folder and won't appear in the template editor anymore."
        confirmLabel="Delete file"
        variant="destructive"
      />
    </Card>
  );
}

/* ─── 9:16 portrait tile ──────────────────────────────────────────── */

function PortraitTile({
  filename, kind, onPreview, onDelete,
}: {
  filename: string;
  kind: Kind;
  onPreview: () => void;
  onDelete: () => void;
}) {
  // Resolve via the shared helper so the URL picks up
  // VITE_DEFAULT_IMAGES_URL (which already includes the full backend
  // origin in dev). The manifest's bare `baseUrl` is a relative path
  // and would resolve against the admin Vite origin instead.
  const url = resolveAssetUrl(`${kind}/${filename}`);

  return (
    <button
      type="button"
      onClick={onPreview}
      className={cn(
        "group relative isolate aspect-[9/16] overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-100 text-left transition-all duration-300",
        "hover:-translate-y-0.5 hover:border-pink-300 hover:shadow-[0_18px_40px_-12px_rgba(236,72,153,0.45)] dark:border-zinc-700 dark:bg-zinc-900",
      )}
    >
      {/* Image fills the 9:16 frame */}
      <img
        src={url}
        alt={filename}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.opacity = "0.3";
        }}
      />

      {/* Gradient base for legibility */}
      <span aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />

      {/* Hover action stack (top-right) */}
      <div className="absolute right-1.5 top-1.5 z-10 flex flex-col gap-1 opacity-0 transition group-hover:opacity-100">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPreview(); }}
          className="rounded-full bg-black/70 p-1.5 text-white backdrop-blur transition hover:bg-black/90"
          aria-label="Preview"
          title="Preview"
        >
          <Eye className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="rounded-full bg-red-600/90 p-1.5 text-white backdrop-blur transition hover:bg-red-600"
          aria-label="Delete"
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Filename strip (always visible at bottom) */}
      <div className="absolute inset-x-0 bottom-0 z-10 px-2 pb-1.5 pt-3">
        <p className="truncate font-mono text-[10px] font-medium text-white drop-shadow">{filename}</p>
      </div>
    </button>
  );
}

/* ─── Preview lightbox ─────────────────────────────────────────────── */

function PreviewLightbox({
  preview, onClose, onChanged,
}: {
  preview: { kind: Kind; name: string } | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { showToast } = useToast();

  // Close on Escape
  useEffect(() => {
    if (!preview) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [preview, onClose]);

  if (!preview) return null;

  const url = resolveAssetUrl(`${preview.kind}/${preview.name}`);
  const sectionLabel = SECTIONS.find((s) => s.kind === preview.kind)?.label ?? preview.kind;

  const copyRelative = async () => {
    try {
      await navigator.clipboard.writeText(`${preview.kind}/${preview.name}`);
      setCopied(true);
      showToast({ title: "Relative path copied", variant: "success" });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      showToast({ title: "Could not copy", variant: "error" });
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      showToast({ title: "URL copied", variant: "success" });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      showToast({ title: "Could not copy", variant: "error" });
    }
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    try {
      await adminMediaApi.delete(preview.kind, preview.name);
      showToast({ title: "File deleted", description: preview.name, variant: "success" });
      onChanged();
      onClose();
    } catch (e: any) {
      showToast({ title: "Delete failed", description: e?.message, variant: "error" });
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-md"
    >
      {/* Inner panel — stop click propagation so background click doesn't close */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[90vh] w-full max-w-5xl flex-col gap-4 overflow-hidden rounded-3xl border border-white/10 bg-zinc-900 p-4 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.7)] sm:flex-row sm:p-6"
      >
        {/* Preview image — rendered at natural aspect ratio */}
        <div className="flex flex-1 items-center justify-center overflow-hidden rounded-2xl bg-black">
          <img
            src={url}
            alt={preview.name}
            className="max-h-[78vh] w-auto max-w-full object-contain"
          />
        </div>

        {/* Side panel */}
        <div className="flex w-full shrink-0 flex-col gap-4 sm:w-72">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-pink-400">
              {sectionLabel}
            </p>
            <h2 className="mt-1 break-all font-mono text-sm font-semibold text-white">{preview.name}</h2>
            <p className="mt-2 break-all text-[11px] text-zinc-400">{url}</p>
          </div>

          <div className="flex flex-col gap-2">
            <Button variant="primary" size="md" onClick={copyRelative}>
              <Copy className="mr-2 h-3.5 w-3.5" />
              Copy relative path
            </Button>
            <Button variant="secondary" size="md" onClick={copyUrl}>
              <Copy className="mr-2 h-3.5 w-3.5" />
              Copy URL
            </Button>
            <Button variant="ghost" size="md" onClick={() => setConfirmDelete(true)} className="text-red-300 hover:bg-red-500/10">
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              Delete file
            </Button>
          </div>

          {copied && (
            <p className="inline-flex items-center gap-1 text-xs font-medium text-emerald-300">
              <Check className="h-3.5 w-3.5" />
              Copied to clipboard
            </p>
          )}

          <button
            type="button"
            onClick={onClose}
            className="mt-auto inline-flex items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white/70 transition hover:bg-white/10 hover:text-white"
          >
            <X className="h-3.5 w-3.5" />
            Close
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title={`Delete ${preview.name}?`}
        description="This file will be removed from the public folder and won't appear in the template editor anymore."
        confirmLabel="Delete file"
        variant="destructive"
      />
    </div>
  );
}